import { createTheme } from '@mui/material/styles';
import { PropTypes } from '@mui/material';

const colorsFromVariables = {
    inputColor: '#7C7C7C',
    grayInputBackground: '#F2F2F2',
    grayInputDisabled: '#F9F9F9',
    primaryBlue: '#007ACB',
    primaryBlueLowlight: '#007BD2',
    primaryBlueHighlight: '#E6F5FF',
    primaryBlueLight20: '#B3E0FF',
    primaryGreen: '#5CE0A0',
    primaryRed: '#FF5D5C',
    primaryYellow: '#FFB449',
    globalBackground: '#F8F8F8',
    globalShadow: '0 0 10px 2px rgba(0, 0, 0, 0.03)',
    globalShadowDark: '0 0 10px 2px rgba(0, 0, 0, 0.05)',
    fontColor: '#4A4A4A',
    borderColor: '#e8e8e8',
};

const primaryColors = {
    main: colorsFromVariables.primaryBlue,
    light: colorsFromVariables.primaryBlueHighlight,
    lightAlt: colorsFromVariables.primaryBlueLight20,
    contrastText: '#fff',
};

const secondaryColors = {
    main: colorsFromVariables.primaryGreen,
    dark: colorsFromVariables.primaryBlueLowlight,
    contrastText: '#000',
};

const backgroundColors = {
    main: '#F8F8F8',
    nav: '#4A4A4A',
    paper: '#fff',
};

const errorColors = {
    main: colorsFromVariables.primaryRed,
    contrastText: '#fff',
};

const warningColors = {
    main: colorsFromVariables.primaryYellow,
    contrastText: '#fff',
};

const infoColors = {
    main: colorsFromVariables.primaryBlue,
    contrastText: '#fff',
};

const successColors = {
    main: colorsFromVariables.primaryGreen,
    contrastText: '#fff',
};

const textColors = {
    primary: colorsFromVariables.fontColor,
    hyperLink: '#61CBBC',
};

const customColors = {
    borderColor: colorsFromVariables.borderColor,
};

const shadowKeyUmbraOpacity = 0.2;
const shadowKeyPenumbraOpacity = 0.14;
const shadowAmbientShadowOpacity = 0.12;

const createShadow = (...px: number[]): string =>
    [
        `${px[0]}px ${px[1]}px ${px[2]}px ${px[3]}px rgba(0, 0, 0, ${shadowKeyUmbraOpacity})`,
        `${px[4]}px ${px[5]}px ${px[6]}px ${px[7]}px rgba(0, 0, 0, ${shadowKeyPenumbraOpacity})`,
        `${px[8]}px ${px[9]}px ${px[10]}px ${px[11]}px rgba(0, 0, 0, ${shadowAmbientShadowOpacity})`,
    ].join(',');

const placeholderVisible = { opacity: 1, color: '#FF5E5C' };

const placeholder = { opacity: 1, color: '#919191' };

const placeholderHidden = { opacity: 1, color: 'transparent' };

const customGlobals = {
    logo: {
        marginTop: 0,
        height: 41,
        width: 114,
        mainLogo: '', // TODO: add default logo url
    },
    iconFill: {
        light: '#7f8082',
        main: '#D3EDFE',
    },
    cta: {
        variant: 'contained',
        color: 'primary' as PropTypes.Color,
        fullWidth: false,
        hexColor: primaryColors.main,
    },
    input: {
        variant: 'filled',
        color: 'primary' as PropTypes.Color,
    },
    tabs: {
        color: 'primary',
        borderColor: 'primary',
    },
    checkbox: {
        color: 'primary',
    },
};

const theme = createTheme({
    custom: {
        ...customGlobals,
        authView: {
            ...customGlobals,
            nav: {
                top: 120,
            },
            header: {
                backgroundColor: '#fff',
            },
            headings: {
                align: 'left',
                color: 'default',
            },
            joinHeader: {
                headerText: 'How to Join',
                justified: 'left',
            },
        },
        appView: {
            ...customGlobals,
            header: {
                backgroundColor: '#fff',
                contrastColor: primaryColors.main,
            },
            viewHeader: {
                backgroundColor: '#fff',
            },
            nav: {
                defaultIconColor: '#333',
                selectedIconColor: primaryColors.main,
                hoverIconColor: primaryColors.main,
                hoverBackgroundColor: primaryColors.light,
                selectedBackgroundColor: primaryColors.light,
            },
            messaging: {
                sidebar: {
                    inactive: {
                        background: '#fff',
                    },
                    activeTab: {
                        background: backgroundColors.main,
                    },
                    background: '#fff',
                },
                main: {
                    background: backgroundColors.main,
                },
                chat: {
                    background: '#fff',
                },
            },
            settings: {
                cards: {
                    background: '#fff',
                },
            },
            dashboard: {
                headerCard: {
                    background: '#fff',
                },
            },
            postView: {
                header: '#fff',
                footer: '#fff',
                footerText: primaryColors.main,
                cards: '#fff',
                font: '#000',
                tagColor: primaryColors.lightAlt,
                iconColor: primaryColors.lightAlt,
                cardFill: '#f5f5f5',
            },
            payments: {
                tables: {
                    background: '#fff',
                },
            },
            productDetail: 'tabs',
        },
    },
    palette: {
        mode: 'light',
        primary: { ...primaryColors },
        secondary: { ...secondaryColors },
        error: { ...errorColors },
        success: { ...successColors },
        info: { ...infoColors },
        warning: { ...warningColors },
        background: { ...backgroundColors },
        text: { ...textColors },
        custom: { ...customColors },
    },
    breakpoints: {
        values: {
            xs: 0,
            sm: 600,
            md: 960,
            lg: 1280,
            xl: 1920,
        },
    },
    shadows: [
        'none',
        colorsFromVariables.globalShadow,
        '0 -1px 0 #e0e0e0, 0 0 2px rgba(0,0,0,.12), 0 2px 4px rgba(0,0,0,.24)',
        createShadow(0, 1, 3, 0, 0, 1, 1, 0, 0, 2, 1, -1),
        createShadow(0, 1, 5, 0, 0, 2, 2, 0, 0, 3, 1, -2),
        createShadow(0, 1, 8, 0, 0, 3, 4, 0, 0, 3, 3, -2),
        createShadow(0, 2, 4, -1, 0, 4, 5, 0, 0, 1, 10, 0),
        createShadow(0, 3, 5, -1, 0, 5, 8, 0, 0, 1, 14, 0),
        createShadow(0, 3, 5, -1, 0, 6, 10, 0, 0, 1, 18, 0),
        colorsFromVariables.globalShadowDark,
        createShadow(0, 5, 5, -3, 0, 8, 10, 1, 0, 3, 14, 2),
        createShadow(0, 5, 6, -3, 0, 9, 12, 1, 0, 3, 16, 2),
        createShadow(0, 6, 6, -3, 0, 10, 14, 1, 0, 4, 18, 3),
        createShadow(0, 6, 7, -4, 0, 11, 15, 1, 0, 4, 20, 3),
        createShadow(0, 7, 8, -4, 0, 12, 17, 2, 0, 5, 22, 4),
        createShadow(0, 7, 8, -4, 0, 13, 19, 2, 0, 5, 24, 4),
        createShadow(0, 7, 9, -4, 0, 14, 21, 2, 0, 5, 26, 4),
        createShadow(0, 8, 9, -5, 0, 15, 22, 2, 0, 6, 28, 5),
        createShadow(0, 8, 10, -5, 0, 16, 24, 2, 0, 6, 30, 5),
        createShadow(0, 8, 11, -5, 0, 17, 26, 2, 0, 6, 32, 5),
        createShadow(0, 9, 11, -5, 0, 18, 28, 2, 0, 7, 34, 6),
        createShadow(0, 9, 12, -6, 0, 19, 29, 2, 0, 7, 36, 6),
        createShadow(0, 10, 13, -6, 0, 20, 31, 3, 0, 8, 38, 7),
        createShadow(0, 10, 13, -6, 0, 21, 33, 3, 0, 8, 40, 7),
        createShadow(0, 10, 14, -6, 0, 22, 35, 3, 0, 8, 42, 7),
    ],
    shape: {
        borderRadius: 6,
    },
    typography: {
        fontFamily: 'Nunito, sans-serif',
        h1: {
            margin: '24px 0 14px 0',
            fontSize: 22,
            fontWeight: 600,
            color: '#4A4A4A',
            textAlign: 'left',
        },
        h2: {
            fontWeight: 700,
        },
        h3: {
            fontWeight: 700,
        },
        h6: {
            fontWeight: 700,
        },
    },
    components: {
        MuiAppBar: {
            styleOverrides: {
                root: {
                    boxShadow: 'none',
                },
                colorPrimary: {
                    color: 'primary',
                    backgroundColor: 'transparent',
                },
                colorSecondary: {
                    backgroundColor: '#fff',
                },
            },
        },
        MuiTab: {
            styleOverrides: {
                root: {
                    '@media (min-width:960px)': {
                        minWidth: 100,
                    },
                    fontWeight: 400,
                    '@media (min-width:600px)': {
                        padding: '6px 30px 0px 30px',
                        fontSize: 15,
                    },
                },
                textColorInherit: {
                    color: 'inherit',
                    opacity: 0.7,
                    background: 'transparent',
                    '&.Mui-selected': {
                        opacity: 1,
                        color: colorsFromVariables.primaryBlue,
                    },
                    '&.Mui-disabled': {
                        opacity: 0.4,
                    },
                    '&:hover': {
                        opacity: 1,
                        color: colorsFromVariables.primaryBlue,
                    },
                },
            },
        },
        MuiTabs: {
            styleOverrides: {
                root: {
                    backgroundColor: 'transparent',
                },
                scrollButtons: {
                    backgroundColor: 'transparent',
                    color: 'black',
                },
            },
        },
        // These global overrides target internal classes used by MUI X Pickers (e.g., TimePicker/DatePicker).
        // They were originally configured via `components` keys like `MuiPickersClock`, which are no longer exposed in MUI v5+.
        // Since these components don't have public theme keys, we apply style overrides globally using `MuiCssBaseline`.
        MuiCssBaseline: {
            styleOverrides: {
                '@global': {
                    '.MuiClock-pin': {
                        backgroundColor: colorsFromVariables.primaryBlue,
                    },
                    '.MuiClockPointer-pointer': {
                        backgroundColor: colorsFromVariables.primaryBlue,
                    },
                    '.MuiClockPointer-thumb': {
                        border: `14px solid ${colorsFromVariables.primaryBlue}`,
                    },
                    '.MuiClockNumber-root.Mui-selected': {
                        color: '#fff',
                    },
                    '.MuiPickersToolbar-root': {
                        backgroundColor: colorsFromVariables.primaryBlue,
                    },
                    '.MuiPickersCalendar-transitionContainer': {
                        boxSizing: 'border-box',
                    },
                },
            },
        },
        MuiTooltip: {
            styleOverrides: {
                tooltip: {
                    fontSize: 14,
                },
            },
        },
        MuiPickersToolbarText: {
            styleOverrides: {
                root: {
                    '&.Mui-selected': {
                        color: '#fff',
                        fontWeight: 600,
                    },
                },
            },
        },
        MuiPickersToolbarButton: {
            styleOverrides: {
                root: {
                    '&.Mui-selected': {
                        fontWeight: 600,
                    },
                },
            },
        },
        MuiPickersYear: {
            styleOverrides: {
                root: {
                    '&.Mui-selected': {
                        backgroundColor: colorsFromVariables.primaryBlue,
                        '&:hover': {
                            backgroundColor: colorsFromVariables.primaryBlueLowlight,
                        },
                    },
                },
            },
        },
        MuiSelect: {
            styleOverrides: {
                filled: {
                    background: colorsFromVariables.grayInputBackground,
                    borderRadius: 6,
                },
            },
        },
        MuiSwitch: {
            styleOverrides: {
                switchBase: {
                    '&.MuiSwitch-colorPrimary': {
                        left: 5,
                        top: 8,
                        '&:hover': {
                            backgroundColor: 'transparent',
                        },
                    },
                },
                thumb: {
                    boxShadow: 'none',
                    width: 6,
                    height: 6,
                    color: '#fff',
                },
                track: {
                    backgroundColor: '#C6C6C6',
                    width: 20,
                    height: 10,
                    opacity: 1,
                    marginTop: 3,
                },
                colorPrimary: {
                    '&.Mui-checked': {
                        transform: 'translateX(10px)',
                        color: colorsFromVariables.primaryBlue,
                        '&:hover': {
                            backgroundColor: 'transparent',
                        },
                        '& + .MuiSwitch-track': {
                            backgroundColor: colorsFromVariables.primaryBlue,
                            opacity: 1,
                        },
                    },
                    '&.Mui-disabled .MuiSwitch-thumb': {
                        boxShadow: 'none',
                    },
                },
                colorSecondary: {
                    '& .MuiSwitch-thumb': {
                        color: '#EBEBEB',
                        boxShadow: '0px 1px 3px 2px rgba(0, 0, 0, 0.12)',
                    },
                    '& + .MuiSwitch-track': {
                        backgroundColor: '#fff',
                        opacity: 1,
                    },
                    '&.Mui-checked': {
                        color: colorsFromVariables.primaryBlue,
                        '& .MuiSwitch-thumb': {
                            color: colorsFromVariables.primaryBlue,
                        },
                        '& + .MuiSwitch-track': {
                            backgroundColor: colorsFromVariables.primaryBlueLight20,
                            opacity: 1,
                        },
                    },
                },
            },
        },
        MuiCheckbox: {
            styleOverrides: {
                colorSecondary: {
                    '&.Mui-checked': {
                        // Material-UI's checkbox component defaults to the secondary theme color.
                        // So we explicitly set the color here.
                        color: colorsFromVariables.primaryBlue,
                    },
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    fontWeight: 600,
                },
                containedPrimary: {
                    color: '#fff',
                    boxShadow: 'none',
                    borderRadius: 6,
                    padding: '6px 20px',
                    '&:hover': {
                        backgroundColor: colorsFromVariables.primaryBlueLowlight,
                        boxShadow: 'none',
                    },
                },
                containedSecondary: {
                    color: '#fff',
                    boxShadow: 'none',
                    borderRadius: 6,
                    padding: '6px 20px',
                    '&:hover': {
                        backgroundColor: colorsFromVariables.primaryBlueLowlight,
                        boxShadow: 'none',
                    },
                },
            },
        },
        MuiFilledInput: {
            styleOverrides: {
                root: {
                    backgroundColor: colorsFromVariables.grayInputBackground,
                    '&:hover': {
                        backgroundColor: colorsFromVariables.grayInputBackground,
                    },
                    '&.Mui-focused': {
                        backgroundColor: colorsFromVariables.grayInputBackground,
                    },
                    borderRadius: 6,
                    border: 'none',
                },
                input: {
                    padding: 10,
                    backgroundColor: colorsFromVariables.grayInputBackground,
                    '&.Mui-disabled': {
                        backgroundColor: colorsFromVariables.grayInputDisabled,
                    },
                },
                adornedEnd: {
                    paddingRight: 0,
                },
                underline: {
                    '&:after': {
                        borderBottom: `2px solid ${colorsFromVariables.primaryBlue}`,
                        left: 0,
                        bottom: 0,
                        content: '""',
                        position: 'absolute',
                        right: 0,
                        transform: 'scaleX(0)',
                        pointerEvents: 'none',
                    },
                    '&.Mui-focused:after': {
                        transform: 'scaleX(1)',
                    },
                    '&.Mui-error:after': {
                        borderBottomColor: colorsFromVariables.primaryRed,
                    },
                    '&:before': {
                        borderBottom: 'transparent',
                        left: 0,
                        bottom: 0,
                        content: '"\\00a0"',
                        position: 'absolute',
                        right: 0,
                        pointerEvents: 'none',
                    },
                    '&:hover:not(.Mui-disabled):not(.Mui-focused):not(.Mui-error):before': {
                        borderBottom: 'transparent',
                    },
                    '&:hover:not(.Mui-disabled):before': {
                        borderBottom: 'none',
                    },
                    '&.Mui-disabled:before': {
                        borderBottom: 'none',
                        borderBottomStyle: 'unset',
                    },
                },
                multiline: {
                    padding: 0,
                    position: 'absolute',
                    zIndex: 100,
                    boxShadow: colorsFromVariables.globalShadow,
                },
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    height: 35,
                    borderRadius: 10,
                    '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'inherit',
                        borderWidth: 1,
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                        borderWidth: 1,
                    },
                },
                input: {},
            },
        },
        MuiAccordion: {
            styleOverrides: {
                root: {
                    '&:before': {
                        position: 'absolute',
                        left: 0,
                        top: -1,
                        right: 0,
                        height: 1,
                        content: '""',
                        opacity: 1,
                    },
                },
                rounded: {
                    borderRadius: 6,
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                rounded: {
                    borderRadius: 6,
                },
            },
        },
        MuiCollapse: {
            styleOverrides: {
                entered: {
                    borderBottomLeftRadius: 6,
                    borderBottomRightRadius: 6,
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    color: '#7c7c7c',
                    margin: '5px 8px 5px 0',
                    '&.MuiChip-colorPrimary': {
                        color: '#7c7c7c',
                        boxShadow: colorsFromVariables.globalShadow,
                        backgroundColor: '#fff',
                        '& .inf-attributes-icon': {
                            color: colorsFromVariables.primaryBlue,
                        },
                    },
                    '&.MuiChip-colorSecondary': {
                        backgroundColor: '#ebebeb',
                    },
                    '&.MuiChip-deletable': {
                        '&.MuiChip-colorPrimary:focus': {
                            backgroundColor: '#c7c7c7',
                        },
                        '&.MuiChip-colorSecondary:focus': {
                            backgroundColor: '#c7c7c7',
                        },
                    },
                },
                label: {
                    paddingLeft: 8,
                    paddingRight: 8,
                },
                icon: {
                    marginLeft: 8,
                    marginRight: 0,
                    color: '#7c7c7c',
                    '&.MuiChip-iconColorPrimary': {
                        color: colorsFromVariables.primaryBlue,
                    },
                    '&.MuiChip-iconColorSecondary': {
                        color: '#7c7c7c',
                    },
                },
                deleteIconColorSecondary: {
                    color: '#7c7c7c',
                    '&:hover': {
                        color: colorsFromVariables.fontColor,
                    },
                },
                deletableColorSecondary: {
                    '&:focus': {
                        backgroundColor: '#c7c7c7',
                    },
                },
                iconColorSecondary: {
                    color: '#7c7c7c',
                },
                colorSecondary: {
                    backgroundColor: '#ebebeb',
                },
                deleteIcon: {
                    fontSize: 18,
                    marginRight: 8,
                    marginLeft: 0,
                    '&.MuiChip-deleteIconColorPrimary': {
                        color: colorsFromVariables.fontColor,
                    },
                    '&.MuiChip-deleteIconColorSecondary': {
                        color: '#7c7c7c',
                        '&:hover': {
                            color: colorsFromVariables.fontColor,
                        },
                    },
                },
            },
        },
        MuiInput: {
            styleOverrides: {
                underline: {
                    '&:after': {
                        borderBottom: `2px solid ${colorsFromVariables.primaryBlue}`,
                        left: 0,
                        bottom: 0,
                        content: '""',
                        position: 'absolute',
                        right: 0,
                        transform: 'scaleX(0)',
                        pointerEvents: 'none',
                    },
                    '&.Mui-focused:after': {
                        transform: 'scaleX(1)',
                    },
                    '&.Mui-error:after': {
                        borderBottomColor: colorsFromVariables.primaryRed,
                    },
                    '&:before': {
                        borderBottom: 'transparent',
                        left: 0,
                        bottom: 0,
                        content: '"\\00a0"',
                        position: 'absolute',
                        right: 0,
                        pointerEvents: 'none',
                    },
                    '&:hover:not(.Mui-disabled):not(.Mui-focused):not(.Mui-error):before': {
                        borderBottom: 'transparent',
                    },
                    '&.Mui-disabled:before': {
                        borderBottom: 'transparent',
                    },
                    '&:hover:not(.Mui-disabled):before': {
                        borderBottom: 'transparent',
                    },
                },
            },
        },
        MuiInputBase: {
            styleOverrides: {
                root: {
                    color: colorsFromVariables.inputColor,
                    background: '#fff',
                    borderRadius: 6,
                    overflow: 'hidden',
                },
                input: {
                    background: '#fff',
                    width: '100%',
                    padding: 10,
                    fontSize: 15,
                    borderRadius: 6,
                    '&::-webkit-input-placeholder': placeholder,
                    '&::-moz-placeholder': placeholder, // Firefox 19+
                    '&:-ms-input-placeholder': placeholder, // IE 11
                    '&::-ms-input-placeholder': placeholder, // Edge
                    '&:focus': {
                        outline: 0,
                    },
                    'label[data-shrink=false] + .MuiInputBase-formControl &': {
                        '&::-webkit-input-placeholder': placeholderHidden,
                        '&::-moz-placeholder': placeholderHidden, // Firefox 19+
                        '&:-ms-input-placeholder': placeholderHidden, // IE 11
                        '&::-ms-input-placeholder': placeholderHidden, // Edge
                        '&:focus::-webkit-input-placeholder': placeholderVisible,
                        '&:focus::-moz-placeholder': placeholderVisible, // Firefox 19+
                        '&:focus:-ms-input-placeholder': placeholderVisible, // IE 11
                        '&:focus::-ms-input-placeholder': placeholderVisible, // Edge
                    },
                },
            },
        },
        MuiPickersDay: {
            styleOverrides: {
                root: {
                    '&.Mui-selected': {
                        color: 'white',
                        backgroundColor: colorsFromVariables.primaryBlue,
                        '&:hover': {
                            backgroundColor: colorsFromVariables.primaryBlueLowlight,
                        },
                    },
                    '&.MuiPickersDay-today': {
                        color: colorsFromVariables.primaryBlue,
                    },
                },
            },
        },
        MuiBadge: {
            styleOverrides: {
                badge: {
                    color: '#fff',
                    backgroundColor: colorsFromVariables.primaryRed,
                },
            },
        },
        MuiDrawer: {
            styleOverrides: {
                paperAnchorDockedLeft: {
                    borderRight: 'none',
                },
            },
        },
        MuiFormControlLabel: {
            styleOverrides: {
                root: {
                    marginRight: 0,
                },
            },
        },
        MuiFormControl: {
            styleOverrides: {
                root: {
                    borderRadius: 6,
                },
            },
        },
        MuiFormLabel: {
            styleOverrides: {
                root: {
                    '&.Mui-focused': {
                        color: 'inherit',
                    },
                    '&.Mui-error': {
                        color: 'inherit',
                    },
                },
            },
        },
        MuiCardHeader: {
            styleOverrides: {
                root: {
                    padding: '25px 30px 15px 30px',
                },
            },
        },
        MuiCardContent: {
            styleOverrides: {
                root: {
                    padding: '15px 30px 10px 30px',
                },
            },
        },
        MuiCardActions: {
            styleOverrides: {
                root: {
                    padding: '10px 30px 10px 30px',
                },
            },
        },
        MuiClock: {
            styleOverrides: {
                pin: {
                    backgroundColor: colorsFromVariables.primaryBlue,
                },
            },
        },
        MuiClockNumber: {
            styleOverrides: {
                root: {
                    '&.Mui-selected': {
                        color: '#fff',
                    },
                },
            },
        },
        MuiPickersLayout: {
            styleOverrides: {
                toolbar: {
                    backgroundColor: colorsFromVariables.primaryBlue,
                },
            },
        },
    },
});

export default theme;
